import { Link, useLocation, useNavigate } from 'react-router-dom';
import { type MouseEvent, useEffect, useState } from 'react';
import { Menu, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getAuthToken } from '@/lib/auth-client';
import { getMe } from '@/lib/api-client';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { CountrySelector } from '@/components/CountrySelector';
import { useLanguage, SUPPORTED_LANGUAGES, LanguageFlagIcon } from '@/lib/language-context';
import { useTranslation } from '@/lib/use-translation';
import type { TranslationKey } from '@/lib/translations';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useLogout } from '@/lib/logout-context';

type MainNavItem = {
    label: string;
    href: string;
    scrollTargetId?: string;
};

function getMainNavItems(t: (key: TranslationKey) => string) {
    return [
        { label: t('nav.home'), href: '/' },
        { label: t('nav.tools'), href: '/tools' },
        { label: t('nav.courseContent'), href: '/#course-content', scrollTargetId: 'course-content' },
        { label: t('nav.pricing'), href: '/pricing' },
        { label: t('nav.newsAndBlogs'), href: '/news' },
        { label: t('nav.events'), href: '/events' },
    ] satisfies MainNavItem[];
}

function getProfileLinks(t: (key: TranslationKey) => string) {
    return [
        { label: t('nav.dashboard'), href: '/dashboard/overview' },
        { label: t('nav.myCourses'), href: '/dashboard/my-courses' },
        { label: t('nav.profile'), href: '/dashboard/profile' },
        { label: t('nav.certificates'), href: '/dashboard/certificates' },
        { label: t('nav.community'), href: '/dashboard/community' },
        { label: t('nav.rewards'), href: '/dashboard/rewards' },
    ];
}

type StoredUser = {
    fullName?: string | null;
    email?: string | null;
    profileImageUrl?: string | null;
};

export function SiteHeader() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { logoutWithOverlay } = useLogout();

    const [isScrolled, setIsScrolled] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userName, setUserName] = useState<string>('Student');
    const [userInitials, setUserInitials] = useState<string>('ST');
    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

    const mainNavItems = getMainNavItems(t);
    const profileLinks = getProfileLinks(t);

    const applyStoredUser = (user: StoredUser) => {
        const name = user.fullName || user.email || 'Student';
        setUserName(name);
        setProfileImageUrl(user.profileImageUrl ?? null);

        const parts = name.trim().split(/\s+/);
        const initials =
            (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
        setUserInitials(initials.toUpperCase() || 'ST');
    };

    useEffect(() => {
        function handleScroll() {
            setIsScrolled(window.scrollY > 10);
        }
        handleScroll();
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    async function fetchUserData(token: string) {
        try {
            const userData = await getMe(token);
            if (userData) {
                const name = userData.fullName || userData.email || 'Student';
                setUserName(name);
                
                const parts = name.trim().split(/\s+/);
                const initials = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
                setUserInitials(initials.toUpperCase() || 'ST');
                
                // Store in localStorage for future use
                localStorage.setItem('airies_user', JSON.stringify({
                    fullName: userData.fullName,
                    email: userData.email,
                    authType: userData.authType,
                }));
            }
        } catch {
            // If API fails, fallback to defaults
            setUserName('Student');
            setUserInitials('ST');
        }
    }

    // Check auth status (token + user info)
    useEffect(() => {
        const token = getAuthToken();
        if (token && typeof window !== 'undefined') {
            queueMicrotask(() => setIsAuthenticated(true));
            const raw = localStorage.getItem('airies_user');
            if (raw) {
                try {
                    const parsed: StoredUser = JSON.parse(raw);
                    queueMicrotask(() => applyStoredUser(parsed));
                } catch {
                    // If parsing fails, try to fetch from API
                    queueMicrotask(() => {
                        void fetchUserData(token);
                    });
                }
            } else {
                // If no user data in localStorage, fetch from API
                queueMicrotask(() => {
                    void fetchUserData(token);
                });
            }
        } else {
            queueMicrotask(() => {
                setIsAuthenticated(false);
                setUserName('Student');
                setUserInitials('ST');
                setProfileImageUrl(null);
            });
        }
    }, [pathname]);

    const isActive = (href: string) =>
        href === '/' ? pathname === '/' : pathname.startsWith(href);

    const closeDrawer = () => setIsDrawerOpen(false);

    const handleMainNavClick = (
        event: MouseEvent<HTMLAnchorElement>,
        item: MainNavItem,
        shouldCloseDrawer = false,
    ) => {
        if (!item.scrollTargetId) {
            if (shouldCloseDrawer) closeDrawer();
            return;
        }

        event.preventDefault();

        const finish = () => {
            if (shouldCloseDrawer) closeDrawer();
        };

        if (pathname === '/') {
            const element = document.getElementById(item.scrollTargetId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                navigate(`/#${item.scrollTargetId}`);
            }
            finish();
            return;
        }

        navigate(`/#${item.scrollTargetId}`);
        finish();
    };

    const handleLogout = async () => {
        await logoutWithOverlay({
            onBeforeNavigate: () => {
                setIsAuthenticated(false);
                closeDrawer();
            },
        });
    };

    return (
        <div className="fixed top-0 z-50 w-full">
            {/* Top announcement bar (scrolls away, not fixed) */}
            {/* <div className="w-full bg-gradient-to-r from-[#6689FF] to-[#537AFC] text-sm text-white p-3">
                <div className="mx-auto flex items-center justify-center">
                    <p className="text-center">
                        <span className="font-normal">Introducing Demo Hubs. Create in-app demos and tours for free. Unlimited viewers, instant
                          setup.</span>&nbsp;&nbsp;
                        <Button className="font-normal underline-offset-2 bg-[#4568DC] px-3 py-1 rounded-full text-xs">
                            Learn more →
                        </Button>
                    </p>
                </div>
            </div> */}

            {/* Main nav (sticky with glass effect on scroll) */}
            <header
                className={cn(
                    'sticky top-0 border-b border-transparent transition-colors',
                    isScrolled
                        ? 'border-slate-100 bg-white/80 !shadow-[0_4px_30px_rgba(0,0,0,0.05)] backdrop-blur-md'
                        : 'bg-white/0',
                )}
            >
                <div className="!mx-auto flex h-16 max-w-[1680px] items-center justify-between !px-5 sm:px-10 md:px-10 lg:px-16 xl:px-20">
                    {/* Logo */}
                    <Link to="/" className="flex flex-1 items-center gap-2">
                        <img src="/images/logo.png" alt="AIRIES" height={28} className="h-7" />
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden items-center justify-center md:flex">
                        <div className="flex items-center gap-1 rounded-full w-full bg-[#F6F8FA] !px-1 !py-1">
                            {mainNavItems.map((item) => (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={(event) => handleMainNavClick(event, item)}
                                    className={cn(
                                        '!rounded-full !px-5 !py-[12px] !text-xs font-medium !text-slate-600 !transition-colors',
                                        (!item.scrollTargetId && isActive(item.href)) &&
                                        '!bg-white !text-slate-900 !shadow-[0px_0px_30px_rgba(0,0,0,0.05)]',
                                    )}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </nav>

                    {/* Desktop right-side actions */}
                    <div className="hidden flex-1 items-center justify-end gap-4 md:flex">
                        <CountrySelector />
                        <LanguageSwitcher variant="header" />

                        {!isAuthenticated ? (
                            <>
                                <Link
                                    to="/login"
                                    className="!text-[14px] font-medium !text-slate-600 hover:!text-slate-900"
                                >
                                    {t('nav.signIn')}
                                </Link>

                                <Button
                                    asChild
                                    className="!h-auto !rounded-[8px] !bg-[#4568DC] !px-4 !py-2.5 !text-[14px] !text-white font-semibold shadow-md hover:!bg-[#3b5ad0]"
                                >
                                    <Link to="/get-started">{t('nav.getStarted')} →</Link>
                                </Button>
                            </>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="!flex items-center !gap-2 !overflow-hidden !rounded-full !text-xs font-medium !text-slate-700">
                                        <Avatar className="!h-9 !w-9 !rounded-full !bg-[#4568DC] !shrink-0">
                                            {profileImageUrl && <AvatarImage src={profileImageUrl} alt="" className="!rounded-full" />}
                                            <AvatarFallback className="!rounded-full !bg-[#4568DC] !text-xs !font-semibold !text-white">
                                                {userInitials}
                                            </AvatarFallback>
                                        </Avatar>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="!w-56 !bg-white !p-2 !shadow-lg !rounded-lg">
                                    <div className="!px-2 !py-2 !text-xs !text-slate-500">
                                        {t('nav.signedInAs')}
                                        <div className="!truncate !text-sm font-medium !text-slate-900">
                                            {userName}
                                        </div>
                                    </div>
                                    <DropdownMenuSeparator />
                                    {profileLinks.map((link) => (
                                        <DropdownMenuItem
                                            key={link.href}
                                            asChild
                                            className="!text-sm font-medium !text-slate-700 !cursor-pointer hover:!bg-slate-50 !p-2"
                                        >
<Link to={link.href}>
                                            {link.label}
                                            </Link>
                                        </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={handleLogout}
                                        className="!text-red-600 !focus:!text-red-600 !cursor-pointer !p-2 hover:!bg-slate-50"
                                    >
                                        {t('nav.logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>

                    {/* Mobile: language + hamburger + user icon (visual only) */}
                    <div className="!flex !items-center !gap-2 md:!hidden">
                        <CountrySelector variant="mobile" />
                        <MobileLanguageSwitcher />
                        <button
                            onClick={() => setIsDrawerOpen((v) => !v)}
                            className="!inline-flex !h-9 !w-9 !items-center !justify-center !rounded-full !border !border-slate-200 !bg-white !text-slate-700"
                        >
                            <span className="!sr-only">{t('nav.toggleMenu')}</span>
                            {isDrawerOpen ? (
                                <X className="!h-4 !w-4" />
                            ) : (
                                <Menu className="!h-4 !w-4" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile drawer + overlay */}
                {isDrawerOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-40 !bg-black/40 !h-[100vh] md:hidden"
                            onClick={closeDrawer}
                        />
                        <div className="!fixed inset-y-0 right-0 !z-50 !w-4/5 !max-w-xs !bg-white !h-[100vh] !shadow-xl md:!hidden">
                            <div className="!flex !items-center !justify-between !px-4 !py-3">
                                <Link to="/" onClick={closeDrawer}>
                                    <img src="/images/logo.png" alt="AIRIES" height={24} className="h-6" />
                                </Link>
                                <button
                                    onClick={closeDrawer}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-700"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            <nav className="!mt-2 !flex !flex-col !gap-1 !px-4 !bg-white">
                                {mainNavItems.map((item) => (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        onClick={(event) => handleMainNavClick(event, item, true)}
                                        className={cn(
                                            '!rounded-lg !px-3 !py-2 !text-sm font-medium !text-slate-600 !hover:!bg-slate-50',
                                            (!item.scrollTargetId && isActive(item.href)) && '!bg-slate-50 !text-slate-900',
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </nav>

                            <div className="!mt-4 !flex !flex-col !gap-2 !px-4 !pb-6 !bg-white">
                                {!isAuthenticated ? (
                                    <>
                                        <Link
                                            to="/login"
                                            onClick={closeDrawer}
                                            className="!block !rounded-full !border !border-slate-200 !px-4 !py-2 !text-center !text-sm font-medium !text-slate-700"
                                        >
                                            {t('nav.signIn')}
                                        </Link>
                                        <Button
                                            asChild
                                            className="!h-10 !w-full !rounded-full !bg-[#4568DC] !text-sm font-medium !hover:!bg-[#3b5ad0] !text-center !text-white"
                                        >
                                            <Link to="/get-started" onClick={closeDrawer} className="!w-full !text-center !text-white">
                                                {t('nav.getStarted')} →
                                            </Link>
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="!mb-2 !flex !items-center !gap-3 !rounded-xl !bg-slate-50 !px-3 !py-2">
                                              <Avatar className="!h-10 !w-10 !shrink-0 !rounded-full !bg-[#4568DC]">
                                                {profileImageUrl && <AvatarImage src={profileImageUrl} alt="" className="!rounded-full" />}
                                                <AvatarFallback className="!rounded-full !bg-[#4568DC] !text-xs !font-semibold !text-white">
                                                    {userInitials}
                                                </AvatarFallback>
                                              </Avatar>
                                            <div className="text-xs">
                                                <div className="font-medium text-slate-900">
                                                    {userName}
                                                </div>
                                                <div className="text-slate-500">
                                                    {t('nav.signedInToAiries')}
                                                </div>
                                            </div>
                                        </div>

                                        {profileLinks.map((link) => (
                                            <Button
                                                key={link.href}
                                                variant="ghost"
                                                className="!justify-start !rounded-lg !px-3 !text-sm font-medium !text-slate-700"
                                                onClick={() => {
                                                    closeDrawer();
                                                    navigate(link.href);
                                                }}
                                            >
                                                {link.label}
                                            </Button>
                                        ))}

                                        <Button
                                            variant="ghost"
                                            className="!mt-2 !justify-start !rounded-lg !px-3 !text-sm font-medium !text-red-600 !hover:!bg-red-50"
                                            onClick={handleLogout}
                                        >
                                            {t('nav.logout')}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </header>
        </div>
    );
}

function MobileLanguageSwitcher() {
    const { locale, setLocale } = useLanguage();
    const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === locale) ?? SUPPORTED_LANGUAGES[0];

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors">
                    <LanguageFlagIcon code={currentLang.code} className="!h-5 !w-5" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px] bg-white">
                {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                        key={lang.code}
                        onClick={() => setLocale(lang.code)}
                        className="flex items-center justify-between gap-2 !p-2"
                    >
                        <span className="flex items-center gap-2">
                            <LanguageFlagIcon code={lang.code} className="!h-5 !w-5" />
                            <span>{lang.label}</span>
                        </span>
                        <Check
                            className={cn(
                                'h-4 w-4 text-[#4568DC]',
                                locale === lang.code ? 'opacity-100' : 'opacity-0',
                            )}
                        />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
